// src/pages/Reports.js
import { useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import { AuthContext } from "../contexts/AuthContext";
import {
  apiFetch,
  apiFetchForOrg,
  fetchOrganizations,
  getSelectedOrgId,
  setSelectedOrgId,
} from "../services/api";
import { CheckCircle, XCircle, Search } from "lucide-react";

// Roles allowed to view/open reports
const REPORT_ROLES = ["superadmin", "admin", "hr", "hiring_manager"];

export default function Reports() {
  const { user } = useContext(AuthContext);
  const role = user?.group_role;
  const isSuper = role === "superadmin";
  const canSeeReports = REPORT_ROLES.includes(role);

  const [orgs, setOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState(getSelectedOrgId()); // null => All (superadmin)

  // raw rows directly from API
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Load orgs (superadmin only) for aggregation mode
  useEffect(() => {
    if (!isSuper) return;
    fetchOrganizations()
      .then((d) => setOrgs(Array.isArray(d) ? d : []))
      .catch(() => setOrgs([]));
  }, [isSuper]);

  // Keep orgFilter synced if changed elsewhere (e.g., Navbar)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "selectedOrgId") setOrgFilter(getSelectedOrgId());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleOrgChange = (e) => {
    const v = e.target.value;
    const next = v === "" ? null : Number(v);
    setOrgFilter(next);
    setSelectedOrgId(next);
  };

  // Fetch reports list
  useEffect(() => {
    let cancelled = false;

    async function loadSingleOrg() {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      try {
        setLoading(true);
        const data = await apiFetch(`/api/reports${qs}`);
        if (!cancelled) setRawRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRawRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    async function loadAllOrgs() {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      try {
        setLoading(true);
        const list = Array.isArray(orgs) ? orgs : [];
        if (list.length === 0) {
          setRawRows([]);
          setLoading(false);
          return;
        }
        const perOrg = await Promise.allSettled(
          list.map((o) =>
            apiFetchForOrg(`/api/reports${qs}`, o.id ?? o.organization_id)
          )
        );
        const merged = perOrg
          .filter((r) => r.status === "fulfilled" && Array.isArray(r.value))
          .flatMap((r) => r.value);
        if (!cancelled) setRawRows(merged);
      } catch {
        if (!cancelled) setRawRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (!canSeeReports) {
      setRawRows([]);
      setLoading(false);
      return;
    }

    if (isSuper && orgFilter == null) loadAllOrgs();
    else loadSingleOrg();

    return () => {
      cancelled = true;
    };
  }, [canSeeReports, isSuper, orgFilter, orgs, from, to]);

  // Map orgId -> name for display
  const orgNameById = useMemo(() => {
    const m = new Map();
    (orgs || []).forEach((o) =>
      m.set(o.id ?? o.organization_id, o.name ?? o.organization_name)
    );
    return m;
  }, [orgs]);

  // Normalize each row to the exact columns + add org for display
  const rows = useMemo(() => {
    const mapped = (rawRows || []).map((r) => toDisplayRow(r, orgNameById));
    if (!q) return mapped;
    const needle = q.trim().toLowerCase();
    return mapped.filter((r) =>
      [
        r.report_id,
        r.candidate_name,
        r.personality_profile,
        r.created_at,
        r.overall_score,
        r.hiring_decision,
        r.organization_label,
      ]
        .filter((x) => x !== null && x !== undefined)
        .map(String)
        .some((s) => s.toLowerCase().includes(needle))
    );
  }, [rawRows, q, orgNameById]);

  // Click → open formatted report route (new tab)
  const openReport = (row) => {
    if (!row?.report_id) return;
    const url = `${window.location.origin}/candidate-report/${row.report_id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      <Navbar />
      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold">Reports</h1>

          {/* Search + date filters */}
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <input
                className="border rounded pl-9 pr-3 py-2 text-sm w-80"
                placeholder="Search (name, profile, decision, organization, …)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <Search
                size={18}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
              />
            </div>
            <label className="text-sm text-gray-500">From:</label>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <label className="text-sm text-gray-500">To:</label>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />

            {/* Superadmin org filter (All/one org) */}
            {isSuper && (
              <>
                <label className="text-sm text-gray-500 ml-2">Organization:</label>
                <select
                  className="border p-2 rounded text-sm"
                  value={orgFilter ?? ""}
                  onChange={handleOrgChange}
                >
                  <option value="">All</option>
                  {orgs.map((o) => (
                    <option key={o.id ?? o.organization_id} value={o.id ?? o.organization_id}>
                      {o.name ?? o.organization_name}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        {!canSeeReports ? (
          <div className="p-6 bg-white rounded border text-red-700">
            403 — You don’t have permission to view reports.
          </div>
        ) : loading ? (
          <div>Loading…</div>
        ) : (
          <table className="w-full bg-white rounded shadow text-left candidate-table">
            <thead>
              <tr>
                {/* Original columns + new Organization column */}
                <th className="p-2 border-b">Report ID</th>
                <th className="p-2 border-b">Candidate Name</th>
                <th className="p-2 border-b">Interview Concluded At</th>
                <th className="p-2 border-b">Personality Profile</th>
                <th className="p-2 border-b">Hiring Score</th>
                <th className="p-2 border-b">Hiring Recommendation</th>
                <th className="p-2 border-b">Organization</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((c, idx) => (
                <tr
                  key={c.report_id ?? idx}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => openReport(c)}
                  title="Open report in a new tab"
                >
                  <td className="p-2 border-b">{c.report_id}</td>
                  <td className="p-2 border-b">{c.candidate_name}</td>
                  <td className="p-2 border-b">{c.created_at}</td>
                  <td className="p-2 border-b">{c.personality_profile}</td>
                  <td className="p-2 border-b">{fmt(c.overall_score)}</td>
                  <td className="p-2 border-b">
                    {String(c.hiring_decision).toUpperCase() === "HIRE" ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle size={18} color="green" />{" "}
                        <b style={{ color: "green" }}>HIRE</b>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <XCircle size={18} color="red" />{" "}
                        <b style={{ color: "red" }}>DO NOT HIRE</b>
                      </span>
                    )}
                  </td>
                  <td className="p-2 border-b">{c.organization_label}</td>
                </tr>
              ))}
              {(!rows || rows.length === 0) && (
                <tr>
                  <td className="p-3 text-gray-500" colSpan={7}>
                    No reports found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "—";
  return String(v);
}

// Convert one API row into the display row your table needs, with organization label.
function toDisplayRow(r, orgNameById) {
  const reportId = r?.interview_report_id ?? r?.id ?? null;

  const createdAt = r?.created_at
    ? new Date(r.created_at).toLocaleString()
    : "";

  let body = r?.report_json;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      body = { raw: r.report_json };
    }
  }
  body = body || {};

  const candidateName =
    body.candidate_name ||
    body.candidate ||
    (typeof body.candidate_details === "object"
      ? body.candidate_details?.name
      : undefined) ||
    r?.candidate_name ||
    r?.candidate_id ||
    "—";

  const personality =
    (body.personality_profile && body.personality_profile.myers_briggs) ||
    body.myers_briggs ||
    (body.personality_profile && toPersonalitySummary(body.personality_profile)) ||
    body.personality ||
    "—";

  const score =
    body.overall_score ??
    body.hiring_score ??
    body.score ??
    r?.overall_score ??
    null;

  let decision;
  if (r?.report_hiring_recommendation === 1) decision = "HIRE";
  else if (r?.report_hiring_recommendation === 0) decision = "DO NOT HIRE";
  else {
    decision =
      (body.hiring_recommendations && body.hiring_recommendations.decision) ||
      (body.hiring_recommendation && body.hiring_recommendation.decision) ||
      body.decision ||
      "N/A";
  }

  // Organization label: prefer joined Organization; else map by id
  const orgId = r?.organization_id ?? r?.Organization?.organization_id ?? null;
  const orgLabel =
    r?.Organization?.organization_name ??
    (orgId != null ? orgNameById.get(orgId) : null) ??
    (orgId != null ? `#${orgId}` : "—");

  return {
    report_id: reportId,
    candidate_name: candidateName,
    created_at: createdAt,
    personality_profile: personality,
    overall_score: score,
    hiring_decision: decision,
    organization_label: orgLabel,
  };
}

function toPersonalitySummary(pp) {
  if (!pp || typeof pp !== "object") return null;
  const a = pp.myers_briggs ? `MBTI: ${pp.myers_briggs}` : "";
  const b = pp.ocean ? `OCEAN: ${pp.ocean}` : "";
  const s = [a, b].filter(Boolean).join(" | ");
  return s || null;
}
